import { useCallback, useEffect, useState } from 'react';

import dealerApi from '../lib/dealers-api';

import { useConfig } from './useConfig';
import { useBuyingOptions } from './';

export function useDealers({ location, selectedFilters }) {
  const { config } = useConfig();

  const [dealers, setDealers] = useState(null);
  const [dealersPendingAction, setPendingDealersAction] = useState(null);
  const [dealersLastAction, setLastDealersAction] = useState(null);
  const [dealersLoading, setDealersLoading] = useState(!!location);
  const [dealersError, setDealersError] = useState('');
  const [locationDealerCount, setLocationDealerCount] = useState(null);

  const buyingOptionsProduct = useBuyingOptions();

  const loadMoreDealers = useCallback((bounds) => {
    // Bound change can only fetch more dealers
    if (dealers === null) {
      return;
    }

    setPendingDealersAction('bounds');
    setDealersLoading(true);
    const activeFilters = selectedFilters.map(filterId => config.filters.find(item => item.value === filterId).apiValue);
    dealerApi.getDealersByBounds(bounds, {
      countryCode: location.countryCode,
      activeFilters,
      includeServices: config.includeServices,
      heavy: config.isHeavy,
      location,
    }).then(dealerResponse => {
      setDealersLoading(false);
      setDealersError('');
      setLastDealersAction('bounds');
      setPendingDealersAction(null);
      const oldDealers = dealers.map(item => item.uuid);
      const newDealers = [...dealers];
      dealerResponse.forEach(dealer => {
        if (!oldDealers.includes(dealer.uuid)) {
          newDealers.push(dealer);
        }
      });
      if (dealers.length < newDealers.length) {
        newDealers.sort((a, b) => a.distance < b.distance ? -1 : 1);
        setDealers(newDealers);
        console.log('Dealers updated (fetch bounds)', newDealers);
      }
    }).catch(e => {
      if (e === 'REQUEST_CANCELED') {
        return;
      }
      setDealersLoading(false);
      setDealersError(e.message);
      setDealers([]);
      setPendingDealersAction(null);
    });
  }, [dealers, selectedFilters, location, config.includeServices, config.isHeavy, config.filters]);

  useEffect(() => {
    if (!location) {
      return;
    }

    const activeFilters = selectedFilters.map(filterId => config.filters.find(item => item.value === filterId).apiValue);

    setPendingDealersAction('location');
    setDealersLoading(true);
    dealerApi.getDealersByLocation(location, {
      activeFilters,
      includeServices: config.includeServices,
      heavy: config.isHeavy,
      location,
      productCode: buyingOptionsProduct?.selectedCode || null,
    }).then(dealerResponse => {
      dealerResponse.sort((a, b) => a.distance < b.distance ? -1 : 1);

      setDealersLoading(false);
      setDealersError('');
      setDealers(dealerResponse);
      setLastDealersAction('location');
      setPendingDealersAction(null);
      setLocationDealerCount(dealerResponse.length);
      console.log('Dealers updated (location or search criteria changed)', dealerResponse);
    }).catch(e => {
      if (e === 'REQUEST_CANCELED') {
        return;
      }
      setDealersLoading(false);
      setDealersError(e.message);
      setDealers([]);
      setPendingDealersAction(null);
      setLocationDealerCount(null);
    });
  }, [selectedFilters, location, config.includeServices, config.isHeavy, config.filters]);

  return {
    dealers,
    dealersError,
    dealersLoading,
    dealersPendingAction,
    dealersLastAction,
    locationDealerCount,
    setDealersLoading,
    setDealersError,
    loadMoreDealers,
  };
}
